'use client';

import React, { useEffect, useRef } from "react";
import Image from "next/image";
import type { GallerySection as GallerySectionType } from "@/types/cms";
import SectionHeader from "@/components/sections/utils/SectionHeader";
import { getSectionSpacingClasses } from "@/components/sections/utils/spacing";
import { getCmsImageUrl, toMediaArray, type CmsMediaNode } from "@/lib/cmsHelpers";
import styles from "./GallerySection.module.css";

// Media URLs come through `getCmsImageUrl` which normalizes CMS response
// shapes and applies Cloudinary image optimization before the URL is handed
// to <Image>.
const getMediaUrl = getCmsImageUrl;

const getMediaAlt = (media: CmsMediaNode | null | undefined, fallback: string): string => {
  if (!media) return fallback;
  if (typeof media.alternativeText === "string" && media.alternativeText) {
    return media.alternativeText;
  }
  return fallback;
};

interface GalleryItem {
  desktopSrc?: string | null;
  tabletSrc?: string | null;
  mobileSrc?: string | null;
  alt: string;
}

const buildGalleryItems = (data: GallerySectionType): GalleryItem[] => {
  const desktopArr = toMediaArray(data.imageGalleryDesktop);
  const tabletArr = toMediaArray(data.imageGalleryTablet);
  const mobileArr = toMediaArray(data.imageGalleryMobile);

  const maxLen = Math.max(
    desktopArr.length,
    tabletArr.length,
    mobileArr.length
  );

  const items: GalleryItem[] = [];
  for (let i = 0; i < maxLen; i++) {
    const desktopMedia = desktopArr[i] ?? desktopArr[0];
    const tabletMedia = tabletArr[i] ?? tabletArr[0];
    const mobileMedia = mobileArr[i] ?? mobileArr[0];

    const srcDesktop = desktopMedia ? getMediaUrl(desktopMedia) : null;
    const srcTablet = tabletMedia ? getMediaUrl(tabletMedia) : null;
    const srcMobile = mobileMedia ? getMediaUrl(mobileMedia) : null;

    if (!srcDesktop && !srcTablet && !srcMobile) continue;

    const altBase =
      (desktopMedia && getMediaAlt(desktopMedia, "")) ||
      (tabletMedia && getMediaAlt(tabletMedia, "")) ||
      (mobileMedia && getMediaAlt(mobileMedia, "")) ||
      data.title ||
      "Gallery image";

    items.push({
      desktopSrc: srcDesktop,
      tabletSrc: srcTablet,
      mobileSrc: srcMobile,
      alt: altBase,
    });
  }

  return items;
};

interface Props {
  data: GallerySectionType;
}

const GallerySection: React.FC<Props> = ({ data }) => {
  // Hooks must run unconditionally and in a stable order, so they are declared
  // before any early return. Values they depend on are computed null-safely.
  const appsViewportRef = useRef<HTMLDivElement | null>(null);

  const rawCustomId = data?.gallerySection_customID ?? undefined;
  const normalizedId = rawCustomId?.startsWith("#")
    ? rawCustomId.slice(1)
    : rawCustomId;
  const sectionId = normalizedId || "gallery";

  const isAppsVariant = Boolean(
    normalizedId && normalizedId.trim().toLowerCase() === "apps"
  );

  const spacing = getSectionSpacingClasses(data);

  const widthToken = data?.sectionWidth;
  const widthClass = widthToken ? `w-${widthToken}` : undefined;

  const sectionClass = ['section', spacing].filter(Boolean).join(" ");
  const containerClass = ['section-container', widthClass]
    .filter(Boolean)
    .join(" ");

  const showDivider = false; // no divider boolean on gallery section yet

  const align = data?.contentAlign;
  const textAlignRaw = data?.textAlign;
  const textAlign: "left" | "center" | "right" | undefined =
    textAlignRaw
      ? textAlignRaw.toLowerCase() === "left"
        ? "left"
        : textAlignRaw.toLowerCase() === "right"
        ? "right"
        : "center"
      : undefined;

  // Auto-scroll for apps variant with swipe/scroll support.
  useEffect(() => {
    if (!isAppsVariant) return;

    const el = appsViewportRef.current;
    if (!el) return;

    // Respect reduced-motion preference
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    // Auto-scroll base speed. Keep this high enough to be perceptible.
    const DESKTOP_SPEED = 20; // px/sec on desktop
    const MOBILE_MULTIPLIER = 1.2; // 20% faster on mobile

    const getSpeed = () =>
      window.innerWidth < 768
        ? DESKTOP_SPEED * MOBILE_MULTIPLIER
        : DESKTOP_SPEED;

    let speed = getSpeed();
    let frameId: number;
    let lastTs: number | null = null;
    let startTs: number | null = null;
    let isUserInteracting = false;
    let resumeTimeout: number | null = null;
    let accumulatedScroll = 0; // Accumulate fractional pixels
    // Mirror of el.scrollLeft so the RAF loop only ever *writes* scrollLeft and
    // never reads it back in the same frame (the read-after-write was a
    // forced-reflow source). Resynced from the DOM only when auto-scroll
    // resumes after a user interaction (see markInteraction's resume timeout).
    let currentScroll = el.scrollLeft;

    // Cache scroll geometry outside the RAF loop to avoid forced reflow on
    // every frame. Updated only when the viewport resizes or the scrollable
    // track's content size changes.
    let maxScroll = el.scrollWidth - el.clientWidth;
    const recomputeMaxScroll = () => {
      maxScroll = el.scrollWidth - el.clientWidth;
    };

    const markInteraction = () => {
      isUserInteracting = true;
      if (resumeTimeout !== null) window.clearTimeout(resumeTimeout);
      resumeTimeout = window.setTimeout(() => {
        isUserInteracting = false;
        // Single read, outside the per-frame hot path: pick up wherever the
        // user left the scroll position so the mirror doesn't jump.
        currentScroll = el.scrollLeft;
      }, 1500);
    };

    const handleResize = () => {
      speed = getSpeed();
      recomputeMaxScroll();
    };

    el.addEventListener("wheel", markInteraction, { passive: true });
    el.addEventListener("touchstart", markInteraction, { passive: true });
    el.addEventListener("pointerdown", markInteraction, { passive: true });
    window.addEventListener("resize", handleResize);

    // Observe both the viewport and its first child (the scrollable track)
    // so late content changes (images loading, font swap) update maxScroll.
    const resizeObserver = new ResizeObserver(recomputeMaxScroll);
    resizeObserver.observe(el);
    const track = el.firstElementChild;
    if (track) resizeObserver.observe(track);

    const RAMP_DURATION_MS = 500; // ramp to full speed over 0.5s

    const step = (ts: number) => {
      if (startTs === null) startTs = ts;

      if (lastTs !== null && !isUserInteracting) {
        const dt = (ts - lastTs) / 1000;

        // Smooth ramp-in from 0 → 1 over RAMP_DURATION_MS
        const elapsed = ts - startTs;
        const rampFactor =
          elapsed >= RAMP_DURATION_MS
            ? 1
            : Math.max(0, elapsed / RAMP_DURATION_MS);
        const effectiveSpeed = speed * rampFactor;

        if (maxScroll > 0) {
          // Accumulate fractional pixels so we don't lose precision
          accumulatedScroll += effectiveSpeed * dt;

          // Only apply integer pixel changes
          const pixelsToScroll = Math.floor(accumulatedScroll);
          if (pixelsToScroll > 0) {
            currentScroll += pixelsToScroll;
            accumulatedScroll -= pixelsToScroll; // Keep the fractional part

            // Loop back to start when we reach the end
            if (currentScroll >= maxScroll) {
              currentScroll = 0;
              accumulatedScroll = 0;
            }
            el.scrollLeft = currentScroll; // write only — never read it back
          }
        }
      }
      lastTs = ts;
      frameId = window.requestAnimationFrame(step);
    };

    frameId = window.requestAnimationFrame(step);

    return () => {
      window.cancelAnimationFrame(frameId);
      if (resumeTimeout !== null) window.clearTimeout(resumeTimeout);
      el.removeEventListener("wheel", markInteraction);
      el.removeEventListener("touchstart", markInteraction);
      el.removeEventListener("pointerdown", markInteraction);
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
    };
  }, [isAppsVariant]);

  if (!data) return null;

  const items = buildGalleryItems(data);
  if (!items.length && !data.content) return null;

  if (isAppsVariant && items.length) {
    // Duplicate items multiple times to ensure the track is wider than the viewport
    // so that auto-scroll is always visible even with a small number of icons.
    const loopItems = [...items, ...items, ...items, ...items];

    return (
      <section id={sectionId} className={sectionClass}>
        <div className={containerClass}>
          {showDivider && <div className={styles.divider} />}
          {data.content && (
            <SectionHeader
              content={data.content}
              align={align}
              textAlign={textAlign}
            />
          )}
          <div className={styles.appsRoot}>
            <div className={styles.appsViewport} ref={appsViewportRef}>
              <div className={styles.appsTrack}>
                {loopItems.map((item, idx) => {
                  const src =
                    item.desktopSrc || item.tabletSrc || item.mobileSrc;
                  if (!src) return null;
                  return (
                    <div className={styles.appsIcon} key={`${src}-${idx}`}>
                      <Image
                        src={src}
                        alt={item.alt}
                        width={60}
                        height={60}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Default row-of-images gallery
  return (
    <section id={sectionId} className={sectionClass}>
      <div className={containerClass}>
        {showDivider && <div className={styles.divider} />}
        {data.content && (
          <SectionHeader
            content={data.content}
            align={align}
            textAlign={textAlign}
          />
        )}
        {items.length > 0 && (
          <div className={styles.galleryRow}>
            {items.map((item, idx) => {
              const src =
                item.desktopSrc || item.tabletSrc || item.mobileSrc;
              if (!src) return null;
              return (
                <div className={styles.galleryItem} key={`${src}-${idx}`}>
                  <Image
                    src={src}
                    alt={item.alt}
                    width={640}
                    height={400}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default GallerySection;


